import { collection, doc, query, serverTimestamp, where } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { commitUpdates, getDoc, getDocs, setDoc } from './firestore'
import { db } from './firebase'
import { track } from './analytics'
import { chunk } from './chunk'
import type { Invite, InviteKind } from '@shared/firestore'

const COLLECTION = 'invites'

// A short, opaque, join-code-style id — keeps the share URL tidy and doesn't leak
// the owner's uid the way a deterministic `${uid}_...` id would. 10 Crockford
// base32 chars ≈ 50 bits of entropy: collisions are negligible at our scale, and
// a collision fails the create rather than corrupting anything. Re-share dedup
// is a per-device memo, not a lookup — the id must be ready synchronously.
const ID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ' // Crockford base32 — no I/L/O/U
const ID_LENGTH = 10

function newInviteId(): string {
  const g = globalThis.crypto
  const bytes = new Uint8Array(ID_LENGTH)
  if (g?.getRandomValues) g.getRandomValues(bytes)
  else for (let i = 0; i < ID_LENGTH; i++) bytes[i] = Math.floor(Math.random() * 256)
  // 256 is an exact multiple of 32, so byte % 32 is unbiased.
  let id = ''
  for (const b of bytes) id += ID_ALPHABET[b % 32]
  return id
}

interface InviteOpts {
  ownerId: string
  kind: InviteKind
  eventId: string
  groupId?: string
  /** Memo key only, never written: rotating the group's code rolls every member onto a fresh id. */
  groupInviteCode?: string
}

// Re-sharing the same target reuses the invite already minted for it (#408).
// Per-device, best-effort: another device or a cleared cache mints another doc,
// which cleanupStaleInvites bounds. MEMO_TTL_MS outlasts any festival lead time,
// so a far-ahead share isn't re-minted while the first doc is too young to prune.
const MINTED_PREFIX = 'beacon_invite_minted:'
const MEMO_TTL_MS = 400 * 864e5

function mintedKey(o: InviteOpts): string {
  return `${MINTED_PREFIX}${o.ownerId}|${o.kind}|${o.eventId}|${o.groupId ?? ''}|${o.groupInviteCode ?? ''}`
}

function recallInviteId(key: string): string | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const m = JSON.parse(raw) as { id?: unknown; ts?: unknown }
    if (typeof m.id === 'string' && typeof m.ts === 'number' && Date.now() - m.ts <= MEMO_TTL_MS) return m.id
    localStorage.removeItem(key)
    return null
  } catch {
    return null
  }
}

function rememberInviteId(key: string, id: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ id, ts: Date.now() }))
  } catch {
    // Private mode / storage full — the next share just mints another doc.
  }
}

function forgetInviteId(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Same as above.
  }
}

// Two-phase mint. The id is generated on the client, so the caller has the share
// URL synchronously — critical for the native share sheet and clipboard, which
// need a live user gesture: awaiting the Firestore write first drops that
// activation and silently falls back to copying (the "Share also copies" bug).
// The returned `persist` does the write and can run in the background.
// `kind: 'personal'` shares the owner's own picks; `kind: 'group'` shares a
// group's combined picks and lets a viewer join that group (the owner must be a
// member — enforced by rules).
export function prepareInvite(opts: InviteOpts): { id: string; persist: () => Promise<void> } {
  const key = mintedKey(opts)
  const recalled = recallInviteId(key)
  const id = recalled ?? newInviteId()
  const data: Record<string, unknown> = {
    ownerId: opts.ownerId,
    kind: opts.kind,
    eventId: opts.eventId,
    createdAt: serverTimestamp(),
    revoked: false,
  }
  if (opts.kind === 'group' && opts.groupId) data.groupId = opts.groupId
  return {
    id,
    persist: async () => {
      try {
        await setDoc(doc(db, COLLECTION, id), data as never)
      } catch (err) {
        // A recalled id that was revoked elsewhere can't be re-shared; drop it so a retry mints anew.
        if (recalled) forgetInviteId(key)
        throw err
      }
      rememberInviteId(key, id)
      track('invite_created', { kind: opts.kind })
    },
  }
}

// Turns off every live share link the owner minted for this event, on every
// device. Writes only {revoked: true} so the rules' revoke-only branch applies
// (no group-membership check — the owner may have left the group since).
export async function revokeEventInvites(ownerId: string, eventId: string): Promise<number> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('ownerId', '==', ownerId), where('eventId', '==', eventId)),
  )
  const live = snap.docs.filter(d => d.get('revoked') !== true)
  for (const part of chunk(live, 450)) {
    await commitUpdates(db, part.map(d => ({ ref: d.ref, data: { revoked: true } })))
  }
  try {
    const prefix = `${MINTED_PREFIX}${ownerId}|`
    const stale: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(prefix) && k.includes(`|${eventId}|`)) stale.push(k)
    }
    stale.forEach(k => localStorage.removeItem(k))
  } catch {
    // Storage unavailable — a recalled revoked id fails persist and is dropped then.
  }
  track('invites_revoked', {})
  return live.length
}

/** The invite owner's display name, for the befriend offer; null when unset. */
export async function resolveInviterName(ownerId: string): Promise<string | null> {
  const snap = await getDoc(doc(db, 'users', ownerId))
  return (snap.data() as { displayName?: string } | undefined)?.displayName?.trim() || null
}

export async function resolveInvite(id: string): Promise<Invite | null> {
  const snap = await getDoc(doc(db, COLLECTION, id) as DocumentReference<Omit<Invite, 'id'>>)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

// "Already offered the befriend for this invite" guard. The befriend prompt is a
// one-time offer per invite per device — without this, every reload of a shared
// schedule while signed in would re-toast it. Keyed by invite id.
const OFFERED_PREFIX = 'beacon_invite_befriended:'

export function isBefriendOffered(id: string): boolean {
  try {
    return localStorage.getItem(OFFERED_PREFIX + id) !== null
  } catch {
    return false
  }
}

export function markBefriendOffered(id: string): void {
  try {
    localStorage.setItem(OFFERED_PREFIX + id, '1')
  } catch {
    // Private mode / storage full — the guard is a convenience, not correctness.
  }
}
